import type { DetailLevel, Format } from '@eluve/blocks';
import {
  SAMPLE_PREVISIT_VISIT_NOTES,
  type StructuredPrevisitInsightsInput,
  transformInsightsToSpec,
} from '@eluve/sdui-types';

// ---------------------------------------------------------------------------
// Visit note data (shared with the on-demand preview workflow)
// ---------------------------------------------------------------------------

const visitNotes = SAMPLE_PREVISIT_VISIT_NOTES;

// ---------------------------------------------------------------------------
// Per-variant insight definitions
// ---------------------------------------------------------------------------

interface VariantInsight {
  title: string;
  description: string;
  citationIds: string[];
  citedTextSnippets: Record<string, string>;
}

interface VariantData {
  citationOrder: string[];
  patientSummary: string;
  insights: VariantInsight[];
}

type VariantName = `${Format}_${DetailLevel}`;

const variants: Record<VariantName, VariantData> = {
  paragraph_concise: {
    citationOrder: ['visit-3', 'visit-1', 'visit-2', 'visit-4', 'visit-5'],
    patientSummary:
      'Adult patient with type 2 diabetes mellitus, hypertension, and hyperlipidemia, with recent history of new exertional chest discomfort and persistently suboptimal control of all three conditions.',
    insights: [
      {
        title: 'New exertional chest tightness',
        citationIds: ['visit-3'],
        description:
          'Patient reported intermittent substernal tightness with exertion (stairs, carrying groceries) relieved by rest in June 2026. First-time cardiac symptom in setting of uncontrolled T2DM, HTN, and HLD. Advised ED evaluation if symptoms worsen; patient was hesitant but agreed to monitor.',
        citedTextSnippets: {
          'visit-3':
            'Reports intermittent substernal tightness over past week, occurs with stairs or carrying groceries, improves with rest. No nausea or diaphoresis. First time reporting chest symptoms.',
        },
      },
      {
        title: 'A1c 7.1 \u2192 7.4 \u2192 8.1 \u2192 7.8 \u2192 7.4%',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          'A1c peaked at 8.1% in March 2026 with assessment of poorly controlled T2DM. Most recent A1c 7.4% in September 2026. Patient declined GLP-1 and prefers lifestyle trial; admits inconsistent medication adherence and not checking blood sugars at home.',
        citedTextSnippets: {
          'visit-1': 'A1c 7.4% (prior 7.1)',
          'visit-2': 'A1c 8.1%',
          'visit-4': 'A1c 7.8%',
          'visit-5': 'A1c 7.4% (prior 7.1)',
        },
      },
      {
        title:
          'BP 142/86 \u2192 150/90 \u2192 152/94 \u2192 146/88 \u2192 142/86',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          'BP persistently elevated across visits with peak 152/94 in June 2026. Lisinopril increased to 20 mg in March 2026. Most recent BP 142/86 in September 2026, assessed as borderline.',
        citedTextSnippets: {
          'visit-1': 'BP 142/86',
          'visit-2': 'BP 150/90',
          'visit-3': 'BP 152/94',
          'visit-4': 'BP 146/88',
          'visit-5': 'BP 142/86',
        },
      },
      {
        title: 'LDL 118 \u2192 130 \u2192 122 \u2192 118 mg/dL',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          'LDL peaked at 130 in March 2026 with worsening HLD noted. Most recent LDL 118 in September 2026, still assessed as above goal. Patient on atorvastatin. ASCVD risk was discussed in March 2026.',
        citedTextSnippets: {
          'visit-1': 'LDL 118',
          'visit-2': 'LDL 130',
          'visit-4': 'LDL 122',
          'visit-5': 'LDL 118',
        },
      },
      {
        title: 'Weight 94 \u2192 97 \u2192 99 \u2192 96 \u2192 94 kg',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          'Weight peaked at 99 kg in June 2026 during period of worsening metabolic control, coinciding with chest symptom presentation. Most recent weight 94 kg in September 2026. Patient reports inconsistent diet and not exercising regularly.',
        citedTextSnippets: {
          'visit-1': 'Wt 94 kg',
          'visit-2': 'Wt 97 kg',
          'visit-3': 'Wt 99 kg',
          'visit-4': 'Wt 96 kg',
          'visit-5': 'Wt 94 kg',
        },
      },
      {
        title: 'Medication adherence concern',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          'Patient consistently reports taking medications "most days" or "most of the time" and admits to inconsistent adherence. Also reports not checking blood sugars at home. Previously declined GLP-1 and intensification of diabetes therapy.',
        citedTextSnippets: {
          'visit-1': 'Taking medications most days',
          'visit-2': 'Taking medications inconsistently',
          'visit-4':
            'Not checking blood sugars at home. Taking meds "most of the time."',
          'visit-5': 'Taking medications most days',
        },
      },
    ],
  },
  paragraph_standard: {
    citationOrder: ['visit-3', 'visit-1', 'visit-2', 'visit-4', 'visit-5'],
    patientSummary:
      'Adult patient with type 2 diabetes, hypertension, and hyperlipidemia with history of exertional chest discomfort and suboptimal control of all three conditions.',
    insights: [
      {
        title: 'New exertional chest tightness',
        citationIds: ['visit-3'],
        description:
          'Patient reported intermittent substernal tightness with exertion (stairs, carrying groceries) that improves with rest, first documented on 2026-06-15. This was noted as concerning given cardiovascular risk factors (T2DM, HTN, HLD). Patient was advised to go to ED if symptoms worsen but was hesitant. No follow-up on chest symptoms documented in subsequent visits.',
        citedTextSnippets: {
          'visit-3':
            'Reports intermittent substernal tightness over past week, occurs with stairs or carrying groceries, improves with rest. No nausea or diaphoresis. First time reporting chest symptoms.',
        },
      },
      {
        title:
          'BP 142/86 \u2192 150/90 \u2192 152/94 \u2192 146/88 \u2192 142/86',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          "Blood pressure has been consistently elevated across visits, with peak of 152/94 on 2026-06-15 when chest symptoms were reported. HTN was assessed as 'uncontrolled' on multiple visits. Lisinopril was increased to 20 mg daily on 2026-03-20. Most recent BP 142/86 on 2026-09-20 remains borderline.",
        citedTextSnippets: {
          'visit-1': 'BP 142/86',
          'visit-2': 'BP 150/90',
          'visit-3': 'BP 152/94',
          'visit-4': 'BP 146/88',
          'visit-5': 'BP 142/86',
        },
      },
      {
        title: 'A1c 7.1 \u2192 7.4 \u2192 8.1 \u2192 7.8 \u2192 7.4%',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          "Hemoglobin A1c peaked at 8.1% on 2026-03-20 with assessment of 'poorly controlled' T2DM. GLP-1 agonist was recommended but patient was hesitant. Patient also declined second diabetes agent on 2026-06-22, preferring lifestyle modifications. Most recent A1c 7.4% on 2026-09-20 shows some improvement but remains suboptimal. Patient reports inconsistent medication adherence and poor diet.",
        citedTextSnippets: {
          'visit-1': 'A1c 7.4% (prior 7.1)',
          'visit-2': 'A1c 8.1%',
          'visit-4': 'A1c 7.8%',
          'visit-5': 'A1c 7.4% (prior 7.1)',
        },
      },
      {
        title: 'LDL 118 \u2192 130 \u2192 122 \u2192 118 mg/dL',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          "LDL cholesterol has been persistently above goal despite atorvastatin therapy. ASCVD risk was discussed on 2026-03-20. Given patient's multiple cardiovascular risk factors and recent exertional chest symptoms, lipid management may warrant intensification.",
        citedTextSnippets: {
          'visit-1': 'LDL 118',
          'visit-2': 'LDL 130',
          'visit-4': 'LDL 122',
          'visit-5': 'LDL 118',
        },
      },
      {
        title: 'Weight 94 \u2192 97 \u2192 99 \u2192 96 \u2192 94 kg',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          'Weight peaked at 99 kg on 2026-06-15, coinciding with the visit for exertional chest symptoms. Patient has consistently reported poor diet, no regular exercise, and work-related stress affecting lifestyle. Most recent weight 94 kg on 2026-09-20 shows return to baseline.',
        citedTextSnippets: {
          'visit-1': 'Wt 94 kg',
          'visit-2': 'Wt 97 kg',
          'visit-3': 'Wt 99 kg',
          'visit-4': 'Wt 96 kg',
          'visit-5': 'Wt 94 kg',
        },
      },
      {
        title: 'Medication adherence concern',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          "Patient has repeatedly reported taking medications 'most days' or 'inconsistently' across multiple visits. On 2026-06-22, patient admitted to not checking blood sugars at home and taking meds 'most of the time.' This pattern of partial adherence may be contributing to suboptimal control of diabetes, hypertension, and hyperlipidemia.",
        citedTextSnippets: {
          'visit-1': 'Taking medications most days',
          'visit-2': 'Taking medications inconsistently',
          'visit-4':
            'Not checking blood sugars at home. Taking meds "most of the time."',
          'visit-5': 'Taking medications most days',
        },
      },
    ],
  },
  paragraph_detailed: {
    citationOrder: ['visit-3', 'visit-5', 'visit-1', 'visit-2', 'visit-4'],
    patientSummary:
      'Adult patient with type 2 diabetes, hypertension, and hyperlipidemia with history of new exertional chest discomfort and progressive weight gain over the past year.',
    insights: [
      {
        title: 'New exertional chest tightness reported June 2026',
        citationIds: ['visit-3', 'visit-5'],
        description:
          'Patient reported intermittent substernal tightness over one week in June 2026, occurring with stairs or carrying groceries and improving with rest. This was noted as the first time reporting chest symptoms and was documented as concerning given risk factors (T2DM, HTN, HLD). Patient was advised ED evaluation if symptoms worsen but was hesitant. No follow-up cardiac workup documented in subsequent visits. Most recent visit in September 2026 notes patient denies chest pain.',
        citedTextSnippets: {
          'visit-3':
            'Reports intermittent substernal tightness over past week, occurs with stairs or carrying groceries, improves with rest. No nausea or diaphoresis. First time reporting chest symptoms.',
          'visit-5': 'Denies chest pain, SOB, dizziness.',
        },
      },
      {
        title:
          'BP 142/86 \u2192 150/90 \u2192 152/94 \u2192 146/88 \u2192 142/86',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          'Blood pressure has remained above goal throughout the past year, peaking at 152/94 in June 2026 during the visit for exertional chest discomfort. Hypertension was documented as uncontrolled at multiple visits. Lisinopril was increased to 20 mg daily in March 2026. Most recent BP in September 2026 was 142/86, still elevated but improved from peak. Persistent hypertension noted in context of poorly controlled diabetes and hyperlipidemia increases cardiovascular risk.',
        citedTextSnippets: {
          'visit-1': 'BP 142/86',
          'visit-2': 'BP 150/90',
          'visit-3': 'BP 152/94',
          'visit-4': 'BP 146/88',
          'visit-5': 'BP 142/86',
        },
      },
      {
        title: 'A1c 7.4% \u2192 8.1% \u2192 7.8% \u2192 7.4%',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          'Hemoglobin A1c has fluctuated over the past year, rising from 7.4% in December 2025 to a peak of 8.1% in March 2026, then improving to 7.8% in June 2026 and 7.4% in September 2026. Diabetes control was documented as poorly controlled or worsening at multiple visits. GLP-1 agonist was recommended in March 2026 but patient was hesitant. Patient preferred a lifestyle trial over adding a second diabetes agent in June 2026. Current regimen includes metformin. Patient reports inconsistent medication adherence and diet.',
        citedTextSnippets: {
          'visit-1': 'A1c 7.4% (prior 7.1)',
          'visit-2': 'A1c 8.1%',
          'visit-4': 'A1c 7.8%',
          'visit-5': 'A1c 7.4% (prior 7.1)',
        },
      },
      {
        title: 'LDL 118 \u2192 130 \u2192 122 \u2192 118 mg/dL',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          'LDL cholesterol has remained above goal throughout the year, peaking at 130 in March 2026 and most recently measured at 118 in September 2026. Hyperlipidemia was documented as worsening in March 2026 and above goal at subsequent visits. ASCVD risk was discussed in March 2026. Patient is on atorvastatin. Given history of exertional chest symptoms and multiple cardiovascular risk factors, LDL control remains relevant.',
        citedTextSnippets: {
          'visit-1': 'LDL 118',
          'visit-2': 'LDL 130',
          'visit-4': 'LDL 122',
          'visit-5': 'LDL 118',
        },
      },
      {
        title: 'Weight 94 \u2192 97 \u2192 99 \u2192 96 \u2192 94 kg',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          'Weight increased from 94 kg in December 2025 to a peak of 99 kg in June 2026, then decreased to 94 kg by September 2026. Patient has consistently reported inconsistent diet, no regular exercise, and work stress contributing to poor dietary habits. Weight management is relevant to diabetes, hypertension, and lipid control.',
        citedTextSnippets: {
          'visit-1': 'Wt 94 kg',
          'visit-2': 'Wt 97 kg',
          'visit-3': 'Wt 99 kg',
          'visit-4': 'Wt 96 kg',
          'visit-5': 'Wt 94 kg',
        },
      },
      {
        title: 'Medication adherence concern',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          'Patient has reported inconsistent medication adherence at multiple visits, describing taking medications "most days" or "most of the time." Patient also reports not checking blood sugars at home. This inconsistent adherence may be contributing to suboptimal control of diabetes, hypertension, and hyperlipidemia. Current medications include metformin, lisinopril 20 mg, and atorvastatin.',
        citedTextSnippets: {
          'visit-1': 'Taking medications most days.',
          'visit-2': 'Taking medications inconsistently.',
          'visit-4':
            'Not checking blood sugars at home. Taking meds "most of the time."',
          'visit-5': 'Taking medications most days.',
        },
      },
    ],
  },
  bullet_points_concise: {
    citationOrder: ['visit-3', 'visit-1', 'visit-2', 'visit-4', 'visit-5'],
    patientSummary:
      'Adult patient with type 2 diabetes, hypertension, and hyperlipidemia, with recent history of new exertional chest discomfort and suboptimal control of chronic conditions.',
    insights: [
      {
        title: 'New exertional chest tightness',
        citationIds: ['visit-3'],
        description:
          '\u2022 Intermittent substernal tightness with exertion reported in June 2026, first time experiencing chest symptoms\n\u2022 Advised ED evaluation if symptoms worsen; patient hesitant but agreed to monitor',
        citedTextSnippets: {
          'visit-3':
            'Reports intermittent substernal tightness over past week, occurs with stairs or carrying groceries, improves with rest. No nausea or diaphoresis. First time reporting chest symptoms.',
        },
      },
      {
        title: 'A1c 7.1% \u2192 7.4% \u2192 8.1% \u2192 7.8% \u2192 7.4%',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          '\u2022 A1c peaked at 8.1% in March 2026, most recently 7.4% in September 2026\n\u2022 Patient declined GLP-1 and prefers lifestyle trial; reports inconsistent medication adherence and diet',
        citedTextSnippets: {
          'visit-1': 'A1c 7.4% (prior 7.1)',
          'visit-2': 'A1c 8.1%',
          'visit-4': 'A1c 7.8%',
          'visit-5': 'A1c 7.4% (prior 7.1)',
        },
      },
      {
        title:
          'BP 142/86 \u2192 150/90 \u2192 152/94 \u2192 146/88 \u2192 142/86',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          '\u2022 BP peaked at 152/94 in June 2026 during chest symptom visit; most recent 142/86\n\u2022 Lisinopril increased to 20 mg in March 2026',
        citedTextSnippets: {
          'visit-1': 'BP 142/86',
          'visit-2': 'BP 150/90',
          'visit-3': 'BP 152/94',
          'visit-4': 'BP 146/88',
          'visit-5': 'BP 142/86',
        },
      },
      {
        title: 'LDL 118 \u2192 130 \u2192 122 \u2192 118 mg/dL',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          '\u2022 LDL above goal throughout; ASCVD risk discussed in March 2026\n\u2022 On atorvastatin; no dose adjustment documented',
        citedTextSnippets: {
          'visit-1': 'LDL 118',
          'visit-2': 'LDL 130',
          'visit-4': 'LDL 122',
          'visit-5': 'LDL 118',
        },
      },
      {
        title: 'Weight 94 \u2192 97 \u2192 99 \u2192 96 \u2192 94 kg',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          '\u2022 Weight peaked at 99 kg in June 2026; now back to 94 kg\n\u2022 Patient reports inconsistent diet and not exercising regularly',
        citedTextSnippets: {
          'visit-1': 'Wt 94 kg',
          'visit-2': 'Wt 97 kg',
          'visit-3': 'Wt 99 kg',
          'visit-4': 'Wt 96 kg',
          'visit-5': 'Wt 94 kg',
        },
      },
      {
        title: 'Medication adherence concern',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          '\u2022 Patient reports taking medications "most days" or "most of the time" across multiple visits\n\u2022 Not checking blood sugars at home; declined GLP-1 and second diabetes agent',
        citedTextSnippets: {
          'visit-1': 'Taking medications most days',
          'visit-2': 'Taking medications inconsistently',
          'visit-4':
            'Not checking blood sugars at home. Taking meds "most of the time."',
          'visit-5': 'Taking medications most days',
        },
      },
    ],
  },
  bullet_points_standard: {
    citationOrder: ['visit-3', 'visit-1', 'visit-2', 'visit-4', 'visit-5'],
    patientSummary:
      'Adult patient with type 2 diabetes, hypertension, and hyperlipidemia, with recent history of exertional chest discomfort and ongoing suboptimal control of metabolic conditions.',
    insights: [
      {
        title: 'New exertional chest tightness',
        citationIds: ['visit-3'],
        description:
          '\u2022 Patient reported intermittent substernal tightness with exertion (stairs, carrying groceries) on 2026-06-15, improving with rest\n\u2022 First time reporting chest symptoms; documented as concerning given cardiovascular risk factors (T2DM, HTN, HLD)\n\u2022 Was advised ED evaluation if symptoms worsen; patient was hesitant but agreed to monitor\n\u2022 No follow-up cardiac workup documented in subsequent visits',
        citedTextSnippets: {
          'visit-3':
            'Reports intermittent substernal tightness over past week, occurs with stairs or carrying groceries, improves with rest. No nausea or diaphoresis. First time reporting chest symptoms.',
        },
      },
      {
        title:
          'BP 142/86 \u2192 150/90 \u2192 152/94 \u2192 146/88 \u2192 142/86',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          '\u2022 Blood pressure has remained above goal across all visits, peaking at 152/94 on 2026-06-15\n\u2022 Lisinopril was increased to 20 mg daily on 2026-03-20 due to uncontrolled BP\n\u2022 Most recent reading 142/86 on 2026-09-20 shows some improvement but still elevated\n\u2022 HTN documented as borderline to uncontrolled throughout visits',
        citedTextSnippets: {
          'visit-1': 'BP 142/86',
          'visit-2': 'BP 150/90',
          'visit-3': 'BP 152/94',
          'visit-4': 'BP 146/88',
          'visit-5': 'BP 142/86',
        },
      },
      {
        title: 'A1c 7.4% \u2192 8.1% \u2192 7.8% \u2192 7.4%',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          '\u2022 A1c peaked at 8.1% on 2026-03-20, with diabetes assessed as poorly controlled\n\u2022 GLP-1 agonist was recommended on 2026-03-20 but patient was hesitant; second agent discussed again on 2026-06-22 but patient preferred lifestyle trial\n\u2022 Most recent A1c 7.4% on 2026-09-20 shows improvement but remains above goal\n\u2022 Patient on metformin; not checking blood sugars at home per 2026-06-22 visit',
        citedTextSnippets: {
          'visit-1': 'A1c 7.4% (prior 7.1)',
          'visit-2': 'A1c 8.1%',
          'visit-4': 'A1c 7.8%',
          'visit-5': 'A1c 7.4% (prior 7.1)',
        },
      },
      {
        title: 'LDL 118 \u2192 130 \u2192 122 \u2192 118 mg/dL',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          '\u2022 LDL has remained above goal throughout visits, assessed as worsening on 2026-03-20 at 130 mg/dL\n\u2022 ASCVD risk was discussed on 2026-03-20\n\u2022 Currently on atorvastatin; most recent LDL 118 on 2026-09-20\n\u2022 HLD consistently documented as above goal',
        citedTextSnippets: {
          'visit-1': 'LDL 118',
          'visit-2': 'LDL 130',
          'visit-4': 'LDL 122',
          'visit-5': 'LDL 118',
        },
      },
      {
        title: 'Weight 94 \u2192 97 \u2192 99 \u2192 96 \u2192 94 kg',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          '\u2022 Weight peaked at 99 kg on 2026-06-15, coinciding with presentation of exertional chest symptoms\n\u2022 Patient has reported inconsistent diet and lack of exercise across multiple visits\n\u2022 Work stress noted as contributing factor on 2026-06-22\n\u2022 Most recent weight 94 kg on 2026-09-20',
        citedTextSnippets: {
          'visit-1': 'Wt 94 kg',
          'visit-2': 'Wt 97 kg',
          'visit-3': 'Wt 99 kg',
          'visit-4': 'Wt 96 kg',
          'visit-5': 'Wt 94 kg',
        },
      },
      {
        title: 'Medication adherence concern',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          '\u2022 Patient has reported taking medications inconsistently across multiple visits\n\u2022 2026-03-20: "Taking medications inconsistently"\n\u2022 2026-06-22: Taking meds "most of the time"\n\u2022 2026-09-20: "Taking medications most days"\n\u2022 Non-adherence may be contributing to suboptimal control of T2DM, HTN, and HLD',
        citedTextSnippets: {
          'visit-1': 'Taking medications most days.',
          'visit-2': 'Taking medications inconsistently.',
          'visit-4': 'Taking meds "most of the time."',
          'visit-5': 'Taking medications most days.',
        },
      },
    ],
  },
  bullet_points_detailed: {
    citationOrder: ['visit-3', 'visit-1', 'visit-2', 'visit-4', 'visit-5'],
    patientSummary:
      'Adult patient with type 2 diabetes mellitus, hypertension, and hyperlipidemia with recent history of exertional chest discomfort and ongoing suboptimal control of chronic conditions.',
    insights: [
      {
        title: 'New exertional chest tightness',
        citationIds: ['visit-3'],
        description:
          '\u2022 Patient reported new intermittent substernal tightness occurring with exertion (stairs, carrying groceries) that improves with rest, first documented 2026-06-15.\n\u2022 No associated nausea or diaphoresis reported.\n\u2022 Assessment noted concern for possible cardiac etiology given cardiovascular risk factors (T2DM, HTN, HLD).\n\u2022 Patient was advised ED evaluation if symptoms worsen or recur but was hesitant.\n\u2022 No follow-up cardiac workup documented in subsequent visits; symptom status not reassessed in later notes.',
        citedTextSnippets: {
          'visit-3':
            'Reports intermittent substernal tightness over past week, occurs with stairs or carrying groceries, improves with rest. No nausea or diaphoresis. First time reporting chest symptoms.',
        },
      },
      {
        title:
          'BP 142/86 \u2192 150/90 \u2192 152/94 \u2192 146/88 \u2192 142/86',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          '\u2022 Blood pressure has been consistently elevated across visits from December 2025 through September 2026.\n\u2022 Peak BP of 152/94 recorded on 2026-06-15 coinciding with exertional chest symptoms.\n\u2022 Assessment repeatedly noted HTN as uncontrolled or borderline.\n\u2022 Lisinopril was increased to 20 mg daily in March 2026; most recent reading returned to 142/86.\n\u2022 Patient reports inconsistent medication adherence which may be contributing to variable control.',
        citedTextSnippets: {
          'visit-1': 'BP 142/86',
          'visit-2': 'BP 150/90',
          'visit-3': 'BP 152/94',
          'visit-4': 'BP 146/88',
          'visit-5': 'BP 142/86',
        },
      },
      {
        title: 'A1c 7.4% \u2192 8.1% \u2192 7.8% \u2192 7.4%',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          '\u2022 A1c peaked at 8.1% in March 2026, with assessment noting poorly controlled T2DM.\n\u2022 GLP-1 agonist was recommended at that visit but patient was hesitant.\n\u2022 Patient has declined second diabetes agent, preferring lifestyle trial.\n\u2022 Most recent A1c 7.4% in September 2026 shows improvement but remains above typical targets.\n\u2022 Current regimen is metformin monotherapy.\n\u2022 Patient reports not checking blood sugars at home and inconsistent diet adherence.',
        citedTextSnippets: {
          'visit-1': 'A1c 7.4% (prior 7.1)',
          'visit-2': 'A1c 8.1%',
          'visit-4': 'A1c 7.8%',
          'visit-5': 'A1c 7.4% (prior 7.1)',
        },
      },
      {
        title: 'LDL 118 \u2192 130 \u2192 122 \u2192 118 mg/dL',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          '\u2022 LDL has remained above goal throughout the monitoring period, with assessment noting HLD worsening in March 2026.\n\u2022 ASCVD risk was discussed with patient in March 2026.\n\u2022 Patient is on atorvastatin; no dose adjustment documented.\n\u2022 Given concurrent T2DM, HTN, and recent exertional chest symptoms, lipid control is relevant to cardiovascular risk.',
        citedTextSnippets: {
          'visit-1': 'LDL 118',
          'visit-2': 'LDL 130',
          'visit-4': 'LDL 122',
          'visit-5': 'LDL 118',
        },
      },
      {
        title: 'Weight 94 \u2192 97 \u2192 99 \u2192 96 \u2192 94 kg',
        citationIds: ['visit-1', 'visit-2', 'visit-3', 'visit-4', 'visit-5'],
        description:
          '\u2022 Weight peaked at 99 kg in June 2026 coinciding with exertional chest symptoms.\n\u2022 Patient has returned to baseline of 94 kg as of September 2026.\n\u2022 Consistently reports poor diet, lack of exercise, and work-related stress impacting lifestyle.\n\u2022 No regular exercise documented across all visits.',
        citedTextSnippets: {
          'visit-1': 'Wt 94 kg',
          'visit-2': 'Wt 97 kg',
          'visit-3': 'Wt 99 kg',
          'visit-4': 'Wt 96 kg',
          'visit-5': 'Wt 94 kg',
        },
      },
      {
        title: 'Medication adherence concerns',
        citationIds: ['visit-1', 'visit-2', 'visit-4', 'visit-5'],
        description:
          '\u2022 Patient has repeatedly reported taking medications inconsistently or "most of the time" across multiple visits.\n\u2022 Non-adherence documented in December 2025, March 2026, June 2026, and September 2026.\n\u2022 Current medications include metformin, lisinopril 20 mg, and atorvastatin.\n\u2022 Inconsistent adherence may be contributing to suboptimal control of T2DM, HTN, and HLD.',
        citedTextSnippets: {
          'visit-1': 'Taking medications most days.',
          'visit-2': 'Taking medications inconsistently.',
          'visit-4': 'Taking meds "most of the time."',
          'visit-5': 'Taking medications most days.',
        },
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Convert variant data → StructuredPrevisitInsightsInput → Spec
// ---------------------------------------------------------------------------

function variantToInput(
  variantData: VariantData,
): StructuredPrevisitInsightsInput {
  const noteMap = new Map(visitNotes.map((n) => [n.id, n]));

  return {
    patientSummary: variantData.patientSummary,
    previsitInsights: variantData.insights.map((insight) => ({
      title: insight.title,
      description: insight.description,
      citationIds: insight.citationIds,
      citedTextSnippets: insight.citedTextSnippets,
    })),
    citations: variantData.citationOrder.map((id) => {
      const note = noteMap.get(id);
      if (!note) {
        throw new Error(`Citation order references unknown visit id: "${id}"`);
      }
      return {
        id: note.id,
        type: note.type,
        date: note.date,
        title: note.title,
        data: note.data,
      };
    }),
  };
}

function getVariantName(format: Format, detailLevel: DetailLevel): VariantName {
  return `${format}_${detailLevel}`;
}

/**
 * Returns a PVI spec for the given format + detail level combination.
 */
export function getPreviewSpec(format: Format, detailLevel: DetailLevel) {
  const key = getVariantName(format, detailLevel);
  return transformInsightsToSpec(variantToInput(variants[key]), {
    expandedInsightIndices: [0],
  });
}
